<template>
  <div class="assessment-dimitra-page">
    <breadcrumb :items="breadcrumbs"></breadcrumb>

    <v-container fluid class="page-shell px-6 py-4">
      <div class="d-flex justify-space-between align-center flex-wrap mb-6" style="gap: 16px;">
        <div>
          <h1 class="page-title mb-0">{{ pageTitle }}</h1>
        </div>

        <v-btn
          color="#00BD73"
          dark
          class="text-caption font-weight-bold px-6"
          elevation="0"
          @click="goBack"
        >
          {{ $t("back") || "BACK" }}
        </v-btn>
      </div>

      <div v-if="loading" class="text-center py-12">
        <v-progress-circular
          indeterminate
          color="#00BD73"
          size="48"
        ></v-progress-circular>
      </div>

      <v-alert v-else-if="!detailPayload" type="info" text border="left" class="mb-0">
        {{ $t('esgPlatform.assessmentFarmer.noDimitraFarmDetails') }}
      </v-alert>

      <v-card v-else flat class="detail-card pa-5 pa-md-8">
        <div :class="['Soil','Harvesting'].includes(detailPayload?.moduleName) ? '' : 'detail-layout'">
          <div class="detail-main">
            <!-- <div class="detail-overline mb-2">
              Dimitra {{ detailPayload.moduleName || "Module" }}
            </div> -->
            <h2 class="detail-heading mb-6">
              {{ detailPayload.title || $t("esgPlatform.assessmentFarmer.farmDetails") }}
            </h2>

            <div class="detail-grid">
              <div
                v-for="field in mainFields"
                :key="`field-${field.key}`"
                class="detail-grid-item"
                :id="field._domId"
              >
                <div class="detail-label">
                  {{ getTranslatedFieldLabel(field) }}
                </div>
                <div class="detail-value">
                  <template
                    v-if="
                      isFarmModule(detailPayload?.moduleName) &&
                      String(field.key || '').toLowerCase() === 'location' &&
                      getFarmLocationCoordinateFromValue(field?.rawValue)
                    "
                  >
                    <div class="d-flex align-center flex-wrap" style="gap: 12px;">
                      <span>{{ $t('esgPlatform.assessmentFarmer.point') }}</span>
                      <button
                        type="button"
                        class="action-link-button dimitra-map-link"
                        @click="openFarmLocationDialogWithTitle(detailPayload.title, field?.rawValue)"
                      >
                        {{ $t('esgPlatform.assessmentFarmer.viewOnMap') }}
                      </button>
                    </div>
                  </template>
                  <template
                    v-else-if="
                      isFarmModule(detailPayload?.moduleName) &&
                      String(field.key || '').toLowerCase() === 'farmgeofence' &&
                      getFarmGeofenceCoordinates({ farmGeofence: field?.rawValue }).length >= 3
                    "
                  >
                    <div class="d-flex align-center flex-wrap" style="gap: 12px;">
                      <span>{{ $t('esgPlatform.assessmentFarmer.polygon') }}</span>
                      <button
                        type="button"
                        class="action-link-button dimitra-map-link"
                        @click="openFarmGeofenceDialogWithTitle(detailPayload.title, field?.rawValue)"
                      >
                        {{ $t('esgPlatform.assessmentFarmer.viewOnMap') }}
                      </button>
                    </div>
                  </template>
                  <template v-else>
                    {{ field.value }}
                  </template>
                </div>
              </div>
            </div>
          </div>

            <div class="detail-sidebar" v-if="detailPayload?.moduleName === 'Farms'">            <div v-if="areaField" class="metric-card mb-6">
              <div class="d-flex align-center" style="gap: 5px;">
                <div class="metric-icon">
                  <v-img max-height="300" max-width="300" src="/icons/ph-farm-light.svg" class="img-center"
                  ></v-img>
                </div>
              <div class="metric-label mb-4">{{ $t('esgPlatform.assessmentFarmer.totalArea') }}</div>
               </div>
              <div class="d-flex align-end gap-4" style="gap: 5px;">
                <div class="metric-value">
                  {{ areaField.value === 0 ? "-" : areaField.rawValue.toFixed(1)}}
                </div>
                <div>
                  {{ $t('esgPlatform.assessmentFarmer.acres') }}
                </div>
              </div>
            </div>

            <div v-if="deforestationField?.rawValue.id !== null" class="status-section">
              <div class="status-section-title mb-4">{{ $t('esgPlatform.assessmentFarmer.deforestationStatus') }}</div>
              <div class="status-card">
                <div v-if="deforestationField?.rawValue.deforestationStatus !== null" class="status-card-heading mb-4">
                  {{ $t('esgPlatform.assessmentFarmer.overallDeforestationProbability') }}
                </div>
                <div 
                v-if="deforestationField?.rawValue.deforestationStatus !== null"
                class="status-pill mb-4 mx-auto"
                :class="getStatusClass(deforestationField?.rawValue.deforestationStatus)"
                >
                  {{ deforestationField?.rawValue.deforestationStatus }}
                </div>
                <div class="text-caption mb-5" style="color: #616161;">
                  {{ $t('esgPlatform.assessmentFarmer.reportDate') }}: <span class="font-weight-bold" style="color: #212121;">{{ formatDateShort(deforestationField?.rawValue.created_at) }}</span>
                </div>
                <v-btn
                  outlined
                  class="dimitra-detail-btn"
                  @click="scrollToField(deforestationField?.rawValue.id)"
                >
                  {{ $t('esgPlatform.viewDetails') }}
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </v-card>

      <v-dialog
        v-model="farmGeofenceDialog"
        max-width="980"
        scrollable
      >
        <v-card class="dimitra-detail-dialog">
          <div class="d-flex justify-space-between align-start px-6 pt-6 pb-4 dimitra-detail-dialog-header">
            <div class="pr-4">
              <div class="dimitra-detail-overline mb-2">
                Dimitra {{ farmGeofenceDetails?.moduleName || $t('esgPlatform.farms') }}
              </div>
              <div class="dimitra-detail-title">
                {{ farmGeofenceDetails.title || $t('esgPlatform.assessmentFarmer.farmGeofencing') }}
              </div>
            </div>
            <v-btn icon @click="closeFarmGeofenceDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <v-card-text class="px-6 pt-5 pb-2">
            <div
              ref="farmGeofenceMap"
              class="farm-geofence-map"
            ></div>
          </v-card-text>

          <v-card-actions class="px-6 pb-6 pt-2">
            <v-spacer />
            <v-btn
              outlined
              class="dimitra-detail-btn"
              @click="closeFarmGeofenceDialog"
            >
              {{ $t('esgPlatform.close') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog
        v-model="farmLocationDialog"
        max-width="980"
        scrollable
      >
        <v-card class="dimitra-detail-dialog">
          <div class="d-flex justify-space-between align-start px-6 pt-6 pb-4 dimitra-detail-dialog-header">
            <div class="pr-4">
              <div class="dimitra-detail-overline mb-2">
                {{ $t('esgPlatform.assessmentFarmer.farmLocation') }}
              </div>
              <div class="dimitra-detail-title">
                {{ farmLocationDetails.title || $t('esgPlatform.assessmentFarmer.farmLocation') }}
              </div>
            </div>
            <v-btn icon @click="closeFarmLocationDialog">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>

          <v-card-text class="px-6 pt-5 pb-2">
            <div
              ref="farmLocationMap"
              class="farm-location-map"
            ></div>
          </v-card-text>

          <v-card-actions class="px-6 pb-6 pt-2">
            <v-spacer />
            <v-btn
              outlined
              class="dimitra-detail-btn"
              @click="closeFarmLocationDialog"
            >
              {{ $t('esgPlatform.close') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </div>
</template>

<script>
import moment from "moment";
import { getGoogleMapsLoader } from "@/mixins/GoogleMapLoaderSingleton";

const DETAIL_STORAGE_KEY = "assessmentFarmerDimitraDetails";

export default {
  name: "assessmentFarmerDimitraDetails",
  data() {
    return {
      loading: true,
      detailPayload: null,
      farmGeofenceDialog: false,
      farmGeofenceDetails: {
        moduleName: "",
        title: "",
        coordinates: [],
      },
      farmGeofenceMap: null,
      farmGeofencePolygon: null,
      farmLocationDialog: false,
      farmLocationDetails: {
        title: "",
        coordinate: null,
      },
      farmLocationMap: null,
      farmLocationMarker: null,
      google: null,
      breadcrumbs: [
        {
          text: this.$t("esgPlatform.esgPlatform"),
          link: true,
          exact: true,
          disabled: true,
        },
        {
          text: this.$t("dashboard"),
          disabled: false,
          to: { name: "eudrDashboard" },
        },
      ],
    };
  },
  computed: {
    fields() {
      return Array.isArray(this.detailPayload?.fields)
        ? this.detailPayload.fields
        : [];
    },
    areaField() {
      return this.findField(["area", "total farm size"]);
    },
    deforestationField() {
      return this.findField(["deforestation status"]);
    },
    pageTitle() {
      const moduleName = this.getTranslatedModuleName(this.detailPayload?.moduleName);
      return `${moduleName} ${this.$t("esgPlatform.assessmentFarmer.details")}`;
    },
    mainFields() {
      const isFarms = this.detailPayload?.moduleName === 'Farms';
      const excludedKeys = new Set(
        [isFarms ? this.areaField : null, this.deforestationField]
          .filter(Boolean)
          .map((field) => field.key),
      );
      excludedKeys.add('id');
      
      return this.fields.map((field) => ({
        ...field,
        _domId: `detail-field-${field.key}`,
      })).filter((field) => !excludedKeys.has(field.key));
    },
  },
  mounted() {
    this.loadDetails();
  },
  watch: {
    farmGeofenceDialog(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.renderFarmGeofenceMap();
        });
      } else {
        this.teardownFarmGeofenceMap();
      }
    },
    farmLocationDialog(isOpen) {
      if (isOpen) {
        this.$nextTick(() => {
          this.renderFarmLocationMap();
        });
      } else {
        this.teardownFarmLocationMap();
      }
    },
  },
  methods: {
    loadDetails() {
      try {
        const raw = window.sessionStorage.getItem(DETAIL_STORAGE_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          if (this.matchesCurrentRoute(parsed)) {
            this.detailPayload = parsed;
          }
        }
      } catch (error) {
        this.detailPayload = null;
      } finally {
        this.loading = false;
      }

      if (!this.detailPayload) {
        this.$notify({
          type: "warning",
          text: this.$t("esgPlatform.assessmentFarmer.noDimitraDetailsNotification"),
        });
      }
    },
    matchesCurrentRoute(payload) {
      if (!payload) return false;

      return (
        String(payload.assessmentId || "") === String(this.$route.params.assessmentId || "") &&
        String(payload.goalId || "") === String(this.$route.params.goalId || "") &&
        String(payload.submittedById || "") === String(this.$route.params.submittedById || "")
      );
    },
    findField(labels = []) {
      const normalizedLabels = labels.map((label) => String(label).trim().toLowerCase());

      return this.fields.find((field) =>
        normalizedLabels.includes(String(field.key || field.label || "").trim().toLowerCase()),
      ) || null;
    },
    goBack() {
      this.$router.go(-1);
    },
    scrollToField(fieldKey) {
        this.$router.push({
          name: "EudrAssessmentReport",
          params: { id: fieldKey, type: "assignment" },
        });
    },
    isFarmModule(moduleName) {
      return String(moduleName || "").trim().toLowerCase() === "farms";
    },
    getFarmLocationCoordinateFromValue(location) {
      if (!location || typeof location !== "object") return null;
      const lat = parseFloat(location?.lat ?? location?.latitude);
      const lng = parseFloat(location?.log ?? location?.lng ?? location?.longitude);
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        return { lat, lng };
      }
      return null;
    },
    getFarmGeofenceCoordinates(row = {}) {
      const raw = Array.isArray(row?.farmGeofence) ? row.farmGeofence : [];
      return raw
        .map((coord) => {
          const lat = parseFloat(coord?.lat ?? coord?.latitude);
          const lng = parseFloat(coord?.log ?? coord?.lng ?? coord?.longitude);
          if (Number.isFinite(lat) && Number.isFinite(lng)) {
            return { lat, lng };
          }
          return null;
        })
        .filter(Boolean);
    },
    openFarmGeofenceDialogWithTitle(title, farmGeofence) {
      const coordinates = this.getFarmGeofenceCoordinates({ farmGeofence });
      if (coordinates.length < 3) return;
      this.farmGeofenceDetails = {
        moduleName: "Farms",
        title: title || this.$t("esgPlatform.assessmentFarmer.farmGeofencing"),
        coordinates,
      };
      this.farmGeofenceDialog = true;
    },
    closeFarmGeofenceDialog() {
      this.farmGeofenceDialog = false;
    },
    openFarmLocationDialogWithTitle(title, location) {
      const coordinate = this.getFarmLocationCoordinateFromValue(location);
      if (!coordinate) return;
      this.farmLocationDetails = {
        title: title || this.$t("esgPlatform.assessmentFarmer.farmLocation"),
        coordinate,
      };
      this.farmLocationDialog = true;
    },
    closeFarmLocationDialog() {
      this.farmLocationDialog = false;
    },
    async renderFarmGeofenceMap() {
      try {
        const mapContainer = this.$refs.farmGeofenceMap;
        const coordinates = this.farmGeofenceDetails?.coordinates || [];
        if (!mapContainer || coordinates.length < 3) return;

        const loader = getGoogleMapsLoader();
        const google = await loader.load();
        this.google = google;

        const first = coordinates[0];
        this.farmGeofenceMap = new google.maps.Map(mapContainer, {
          mapTypeId: "satellite",
          center: { lat: first.lat, lng: first.lng },
          zoom: 15,
          disableDefaultUI: true,
          zoomControl: true,
        });

        if (this.farmGeofencePolygon) {
          this.farmGeofencePolygon.setMap(null);
        }

        this.farmGeofencePolygon = new google.maps.Polygon({
          paths: coordinates,
          strokeColor: "#FF6B00",
          strokeOpacity: 0.85,
          strokeWeight: 2,
          fillColor: "#FF6B00",
          fillOpacity: 0.2,
          clickable: false,
          editable: false,
        });
        this.farmGeofencePolygon.setMap(this.farmGeofenceMap);

        const bounds = new google.maps.LatLngBounds();
        coordinates.forEach((coord) => bounds.extend(coord));
        this.farmGeofenceMap.fitBounds(bounds, 60);
      } catch (error) {
        // fail silently; dialog will still open
      }
    },
    teardownFarmGeofenceMap() {
      try {
        if (this.farmGeofencePolygon) {
          this.farmGeofencePolygon.setMap(null);
        }
      } catch (error) {
        // ignore
      }
      this.farmGeofencePolygon = null;
      this.farmGeofenceMap = null;
    },
    async renderFarmLocationMap() {
      try {
        const mapContainer = this.$refs.farmLocationMap;
        const coordinate = this.farmLocationDetails?.coordinate || null;
        if (!mapContainer || !coordinate) return;

        const loader = getGoogleMapsLoader();
        const google = await loader.load();
        this.google = google;

        this.farmLocationMap = new google.maps.Map(mapContainer, {
          mapTypeId: "satellite",
          center: coordinate,
          zoom: 17,
          disableDefaultUI: true,
          zoomControl: true,
        });

        if (this.farmLocationMarker) {
          this.farmLocationMarker.setMap(null);
        }
        this.farmLocationMarker = new google.maps.Marker({
          position: coordinate,
          map: this.farmLocationMap,
        });
      } catch (error) {
        // ignore
      }
    },
    teardownFarmLocationMap() {
      try {
        if (this.farmLocationMarker) {
          this.farmLocationMarker.setMap(null);
        }
      } catch (error) {
        // ignore
      }
      this.farmLocationMarker = null;
      this.farmLocationMap = null;
    },
    formatFieldLabel(label) {
      if (!label) return "-";
      const normalized = String(label).trim().toLowerCase();
      const labelMap = {
        "area planted": this.$t("esgPlatform.assessmentFarmer.areaPlanted"),
        "crop type": this.$t("esgPlatform.assessmentFarmer.cropType"),
        "start date of harvesting": this.$t("esgPlatform.assessmentFarmer.startDateOfHarvesting"),
        "end date of harvesting": this.$t("esgPlatform.assessmentFarmer.endDateOfHarvesting"),
        "total number of days": this.$t("esgPlatform.assessmentFarmer.totalNumberOfDays"),
        "total fresh yield": this.$t("esgPlatform.assessmentFarmer.totalFreshYield"),
        "total dry yield": this.$t("esgPlatform.assessmentFarmer.totalDryYield"),
        "total planned fresh yield": this.$t("esgPlatform.assessmentFarmer.totalPlannedFreshYield"),
        "total planned dry yield": this.$t("esgPlatform.assessmentFarmer.totalPlannedDryYield"),
        "yield for household consumption": this.$t("esgPlatform.assessmentFarmer.yieldForHouseholdConsumption"),
        "yield for sale": this.$t("esgPlatform.assessmentFarmer.yieldForSale"),
        "method of harvesting": this.$t("esgPlatform.assessmentFarmer.methodOfHarvesting"),
      };
      return labelMap[normalized] || label;
    },
    formatDateShort(dateString) {
      if (!dateString) return "-";
      return moment(dateString).format("DD-MM-YYYY");
    },
    getTranslatedModuleName(moduleName) {
      if (!moduleName) {
        return this.$t("esgPlatform.assessmentFarmer.module");
      }

      const translationKey = `esgPlatform.dimitraModuleCatalog.modules.${moduleName}`;
      const translated = this.$t(translationKey);

      return translated !== translationKey ? translated : moduleName;
    },
    getTranslatedFieldLabel(field) {
      const key = field?.key;
      if (key) {
        const keyTranslation = this.$t(`esgPlatform.dimitraModuleCatalog.fields.${key}`);
        if (keyTranslation !== `esgPlatform.dimitraModuleCatalog.fields.${key}`) {
          return keyTranslation;
        }
      }

      return field?.label || field?.key || "-";
    },
    getStatusClass(status) {
    const map = {
      'Very High Deforestation Probability': 'v-high-prob',
      'High Deforestation Probability': 'high-prob',
      'Medium Deforestation Probability': 'medium-prob',
      'Low Deforestation Probability': 'low-prob',
      'Very Low Deforestation Probability': 'v-low-prob',
      'Zero/Negligible Deforestation Probability': 'zero-prob',
      'Manually Mitigated': 'manual-prob'
    };

    return map[status] || '';
  }
  },
};
</script>

<style scoped>
.assessment-dimitra-page {
  background: #f1f4f7;
  min-height: 100vh;
}
.page-shell {
  max-width: 100%;
}
.page-caption {
  color: #7d8a93;
  font-size: 14px;
  font-weight: 500;
}
.page-title {
  color: #101828;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
}
.detail-card {
  background: #ffffff;
  border-radius: 0;
}
.detail-layout {
  display: grid;
  grid-template-columns: minmax(0, 2.1fr) minmax(280px, 420px);
  gap: 28px;
}
.detail-main {
  min-width: 0;
}
.detail-sidebar {
  border-left: 1px solid #e6ece8;
  padding-left: 28px;
}
.detail-overline {
  color: #00bd73;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.detail-heading {
  color: #212121;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.25;
}
.detail-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 28px 36px;
}
.detail-grid-item {
  min-width: 0;
}
.detail-label {
  color: #7d7d7d;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
}
.detail-value {
  color: #151515;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.55;
  word-break: break-word;
}
.metric-card,
.status-card {
  background: #ffffff;
  border: 1px solid #eef2ef;
  border-radius: 24px;
  box-shadow: 0 8px 24px rgba(35, 45, 38, 0.08);
  padding: 28px;
}
.metric-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}
.metric-label {
  color: #7d7d7d;
  font-size: 18px;
  font-weight: 700;
}
.metric-value {
  color: #111111;
  font-size: 34px;
  font-weight: 700;
  line-height: 1.15;
  word-break: break-word;
}
.status-section-title {
  color: #212121;
  font-size: 18px;
  font-weight: 700;
}
.status-card {
  text-align: center;
}
.status-card-heading {
  color: #212121;
  font-size: 14px;
  font-weight: 600;
}
.status-pill {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 28px;
  max-width: fit-content;
  margin-left: auto;
  margin-right: auto;
  padding: 4px 16px;
  border: 1px solid #f7a54a;
  border-radius: 999px;
  background: #fff4e5;
  color: #b46712;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
}
.v-high-prob{
  border: 1px solid #8B0000;
  background: #FF8F8F;
  color: #8B0000;
}
.high-prob{
  border: 1px solid #8B0000;
  background: #FFB6B6;
  color: #8B0000;
}
.medium-prob{
  border: 1px solid #F06B20;
  background: #FFCEB3;
  color: #F06B20;
}
.low-prob{
  border: 1px solid #F06B20;
  background: #FFE8C5;
  color: #F06B20;
}
.v-low-prob{
  border: 1px solid #F49238;
  background: #FFFCEC;
  color: #F49238;
}
.zero-prob{
  border: 1px solid #00BD73;
  background: #E2FFF4;
  color: #00BD73;
}
.manual-prob{
  border: 1px solid #00BD73;
  background: #E2FFF4;
  color: #00BD73;
}
.dimitra-detail-btn {
  border-color: #00bd73 !important;
  color: #00bd73 !important;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  min-width: 132px;
}
.dimitra-detail-btn:hover {
  background-color: #f2fdf8;
}
.action-link-button {
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
}
.dimitra-map-link {
  color: #00bd73;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.01em;
  text-transform: none;
}
.dimitra-map-link:hover {
  text-decoration: underline;
}
.farm-geofence-map,
.farm-location-map {
  width: 100%;
  height: 70vh;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #edf1ee;
}
.dimitra-detail-dialog {
  border-radius: 20px;
}
.dimitra-detail-dialog-header {
  border-bottom: 1px solid #edf1ee;
}
.dimitra-detail-title {
  color: #212121;
  font-size: 24px;
  font-weight: 700;
  line-height: 1.3;
}

@media (max-width: 1264px) {
  .detail-layout {
    grid-template-columns: minmax(0, 1fr);
  }

  .detail-sidebar {
    border-left: none;
    border-top: 1px solid #e6ece8;
    padding-left: 0;
    padding-top: 28px;
  }

  .detail-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 700px) {
  .page-title {
    font-size: 24px;
  }

  .detail-card {
    padding: 20px !important;
  }

  .detail-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 22px;
  }

  .metric-card,
  .status-card {
    padding: 22px;
  }

  .metric-value {
    font-size: 28px;
  }
}
</style>
